import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useDispatch } from 'react-redux'
import * as R from 'ramda'
import { useIntl } from 'react-intl'
import { Editor } from 'react-draft-wysiwyg'
import { EditorState, RichUtils, Modifier, convertToRaw } from 'draft-js'
import { stateFromHTML } from 'draft-js-import-html'
import FontSizeSelector from './customToolbar/FontSizeSelector'
import DoNotTranslate from './customToolbar/DoNotTranslate'
import ColorSelector from './customToolbar/ColorSelector'
import {
  getSelectedBlocksMetadata,
  setBlockData,
  getAllBlocks,
} from 'draftjs-utils'
import MessageAlert from 'app/components/MessageAlert'
import TemplateSelector from 'app/main/dealsPromotions/TemplateSelector'
import { stateToHTML } from 'draft-js-export-html'
import CircularProgress from '@material-ui/core/CircularProgress'
import clsx from 'clsx'
import 'draft-js/dist/Draft.css'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'

import { convertFromHTML } from 'draft-convert'

const DEFAULT_TEXT_ALIGNMENT = `center`
const DEFAULT_FONT_SIZE = 3
const DEFAULT_FONT_COLOR = '#000000'
const MAX_ROWS = 5
const MAX_LENGTH_INPUT = 60

const DONOTTRANSLATE_TAG = 'notranslate'

const useStyles = makeStyles((templateColor) => ({
  editor: {
    '& .DraftEditor-root': {
      width: '100%',
    },
    '& .public-DraftStyleDefault-block': {
      minWidth: '1%',
      margin: '5px',
    },
    '& title["Justify"]': {
      display: 'none',
    },
    '& .public-DraftEditorPlaceholder-root': {
      textAlign: 'center',
    },
    '& .public-DraftEditorPlaceholder-inner': {
      fontSize: '22px',
    },
    '& .public-DraftEditor-content': {
      maxHeight: '300px',
      overflow: 'hidden !important',
    },
    '& .public-DraftStyleDefault-ltr': {
      textAlign: 'unset !important',
    },
    '& .public-DraftStyleDefault-rtl': {
      textAlign: 'unset !important',
    },
  },
  placeholderHide: {
    '& .public-DraftEditorPlaceholder-root ': {
      display: 'none',
    },
  },
  templateLoading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
  },
}))

const stateToHTMLInlineStyleContentState = (editorState) => {
  const contentState = editorState.getCurrentContent()
  return stateToHTML(contentState, {
    blockStyleFn: (block) => {
      let data = block.getData()
      if (data.size === 0) return

      let style = {}
      if (data.get('text-align'))
        style = { ...style, textAlign: data.get('text-align'), width: '100%' }

      return { style }
    },
    inlineStyles: {
      DONOTTRANSLATE: {
        element: 'span',
        attributes: { class: DONOTTRANSLATE_TAG },
      },
    },
    inlineStyleFn: (styles) => {
      const color = styles.filter((value) => value.startsWith('color-')).first()
      const fontSize = styles
        .filter((value) => value.startsWith('fontsize-'))
        .first()

      let styleConfig = {}
      if (color) {
        styleConfig = {
          ...styleConfig,
          color: color.replace('color-', ''),
        }
      }

      if (fontSize) {
        styleConfig = {
          ...styleConfig,
          fontSize: `${fontSize.replace('fontsize-', '')}rem`,
        }
      }
      return {
        element: 'span',
        style: styleConfig,
      }
    },
  })
}

const createFirstState = (value) => {
  if (R.isNil(value)) return EditorState.createEmpty()

  const contentState = convertFromHTML({
    htmlToStyle: (tagTame, element, currentStyle) => {
      currentStyle = currentStyle
        .withMutations((style) => {
          if (element.className === DONOTTRANSLATE_TAG) {
            style.add('DONOTTRANSLATE')
          }

          if (element.style.color) {
            style.add(`color-${element.style.color}`)
          }

          if (element.style.fontSize) {
            const fontSize = R.replace(/rem/g, '', element.style.fontSize)
            style.add(`fontsize-${fontSize}`)
          }
        })
        .toOrderedSet()
      return currentStyle
    },
    htmlToBlock: (tagTame, element) => {
      if (tagTame === 'p') {
        return {
          type: tagTame,
          data: { 'text-align': element.style.textAlign, width: '100%' },
        }
      }
    },
  })(value)
  return EditorState.createWithContent(contentState)
}

const styleMap = {
  DONOTTRANSLATE: {
    borderRadius: '2px',
    padding: '2px',
    background: 'rgb(211, 238, 240)',
  },
}

const getTemplateSelected = ({ templateId, templates }) => {
  const newTemplateList = R.reduce(
    (acc, item) => {
      return [...acc, ...item.templates]
    },
    [],
    templates,
  )

  return templateId
    ? R.find(R.propEq('id', templateId))(newTemplateList)
    : R.head(newTemplateList)
}

const TextEditorDraft = (props) => {
  const classes = useStyles()
  const {
    onChange,
    value,
    onSelectTemplate,
    templateId,
    isDisabled = false,
    templates = [],
  } = props
  const { formatMessage } = useIntl()
  const [editorState, setEditorState] = useState(createFirstState(value))
  const [editorReference, setEditorReference] = useState(null)
  const [isFocus, setIsFocus] = useState(false)
  const dispatch = useDispatch()

  const onUpdateEditorState = (newEditorState) => {
    setEditorState(newEditorState)
  }

  const onFocusEditor = () => {
    setIsFocus(true)
    editorReference.focus()
  }

  const [templateSelected, setTemplateSelected] = useState(undefined)

  useEffect(() => {
    if (R.isNil(templateSelected)) {
      setTemplateSelected(getTemplateSelected({ templateId, templates }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length])

  const changeFontColor = (editorState, newColor, oldColor = null) => {
    let newEditorState = editorState

    if (!R.isNil(oldColor)) {
      const contentStateRemoveColor = Modifier.removeInlineStyle(
        editorState.getCurrentContent(),
        editorState.getSelection(),
        `color-${oldColor}`,
      )

      newEditorState = EditorState.push(
        editorState,
        contentStateRemoveColor,
        'change-inline-style',
      )
    }

    const contentStateChangeColor = Modifier.applyInlineStyle(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      `color-${newColor}`,
    )

    newEditorState = EditorState.push(
      editorState,
      contentStateChangeColor,
      'change-inline-style',
    )

    newEditorState = RichUtils.toggleInlineStyle(
      newEditorState,
      `color-${newColor}`,
    )
    return newEditorState
  }

  const changeFontSize = (editorState, fontSize) => {
    const contentStateChangeFontSize = Modifier.applyInlineStyle(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      `fontsize: ${fontSize}rem;`,
    )

    let newEditorState = EditorState.push(
      editorState,
      contentStateChangeFontSize,
      'change-inline-style',
    )

    newEditorState = RichUtils.toggleInlineStyle(
      newEditorState,
      `fontsize-${fontSize}`,
    )
    return newEditorState
  }

  const onEditorStateChange = (editorStateChange) => {
    let newEditorState = editorStateChange
    const textAlignChange =
      getSelectedBlocksMetadata(newEditorState).get('text-align')
    if (R.isNil(textAlignChange)) {
      const textAlignCurrent =
        getSelectedBlocksMetadata(editorState).get('text-align')
      newEditorState = setBlockData(newEditorState, {
        'text-align': textAlignCurrent,
      })
      onUpdateEditorState(newEditorState)
    }

    const blocks = convertToRaw(editorStateChange.getCurrentContent()).blocks
    const textFromDraft = blocks.map((block) => block.text).join('')

    const currentStyle = newEditorState.getCurrentInlineStyle()
    const color = currentStyle.filter((style) => style.match(/^color-/)).first()

    if (R.isNil(color)) {
      newEditorState = changeFontColor(
        newEditorState,
        R.prop('fontColor', templateSelected),
        color,
      )
      onUpdateEditorState(newEditorState)
    }

    const fontSize = currentStyle
      .filter((style) => style.match(/^fontsize-/))
      .first()

    if (R.isNil(fontSize)) {
      newEditorState = changeFontSize(newEditorState, DEFAULT_FONT_SIZE)
      onUpdateEditorState(newEditorState)
    }

    const blockListLength = getAllBlocks(newEditorState)

    const stateUpdate = R.gte(MAX_ROWS, blockListLength.length)
      ? newEditorState
      : editorState

    onUpdateEditorState(stateUpdate)

    onChange(
      R.isEmpty(textFromDraft)
        ? undefined
        : stateToHTMLInlineStyleContentState(stateUpdate),
    )
  }

  const onSelectedTemplateFromState = (data) => {
    setTemplateSelected(data)
  }

  const customStyleFn = (style, block) => {
    const color = R.unless(
      R.isNil,
      R.replace(/color-/, ''),
      style.filter((style) => style.match(/^color-/)).first(),
    )

    const fontSize = R.unless(
      R.isNil,
      R.replace(/fontsize-/, ''),
      style.filter((style) => style.match(/^fontsize-/)).first(),
    )

    return {
      color:
        color || R.prop('fontColor', templateSelected) || DEFAULT_FONT_COLOR,
      fontSize: `${fontSize || DEFAULT_FONT_SIZE}rem`,
    }
  }

  const handlePastedText = (text, html) => {
    // previous state
    const previousState = editorState
    const previousContent = previousState.getCurrentContent()
    const previousSelection = previousState.getSelection()
    const previousStyle = previousState.getCurrentInlineStyle()

    // new state
    const newStateReplace = EditorState.createEmpty()
    const newSelection = newStateReplace.getSelection()

    //function
    const blockMap = stateFromHTML(html || text).blockMap
    const replaceFragmentState = Modifier.replaceWithFragment(
      previousContent,
      previousSelection,
      blockMap,
    )

    const newStateFromInsertFragment = EditorState.push(
      previousState,
      replaceFragmentState,
      'insert-fragment',
    )

    const currentContentLength = previousContent.getPlainText('')

    const isTextOverMaxLength = R.compose(
      R.lt(MAX_LENGTH_INPUT),
      R.add(R.length(text)),
      R.length,
    )(currentContentLength)

    if (isTextOverMaxLength) {
      const blocks = convertToRaw(
        newStateFromInsertFragment.getCurrentContent(),
      ).blocks
      const textFromDraft = blocks.map((block) => block.text).join('')

      const newText = textFromDraft + text
      const textToInsert = newText.substring(0, MAX_LENGTH_INPUT)

      const newContent = Modifier.replaceText(
        newStateReplace.getCurrentContent(),
        newSelection,
        textToInsert,
        previousStyle,
      )

      let editorStateReplace = EditorState.push(
        newStateReplace,
        newContent,
        'insert-characters',
      )

      const currentStyle = editorStateReplace.getCurrentInlineStyle()
      const color = currentStyle
        .filter((style) => style.match(/^color-/))
        .first()

      if (R.isNil(color)) {
        editorStateReplace = changeFontColor(
          editorStateReplace,
          R.prop('fontColor', templateSelected),
          color,
        )
      }

      const fontSize = currentStyle
        .filter((style) => style.match(/^fontsize-/))
        .first()

      if (R.isNil(fontSize)) {
        editorStateReplace = changeFontSize(
          editorStateReplace,
          DEFAULT_FONT_SIZE,
        )
      }

      onUpdateEditorState(editorStateReplace)
      dispatch(
        MessageAlert.showError({
          message: formatMessage({
            id: 'createVoucher.dialog.editor.textOver',
          }),
        }),
      )
      return 'handled'
    }

    onUpdateEditorState(newStateFromInsertFragment)
  }

  const handleBeforeInput = (text) => {
    const currentContent = editorState.getCurrentContent()
    const currentContentLength = currentContent.getPlainText('')
    const isTextOverMaxLength = R.compose(
      R.lt(MAX_LENGTH_INPUT),
      R.add(R.length(text)),
      R.length,
    )(currentContentLength)

    if (isTextOverMaxLength) {
      return 'handled'
    }
  }

  const onBlurEditor = () => {
    setIsFocus(false)
  }

  useEffect(() => {
    let newEditorState = editorState
    const textAlign =
      getSelectedBlocksMetadata(newEditorState).get('text-align')

    newEditorState = setBlockData(newEditorState, {
      'text-align': R.isNil(textAlign) ? DEFAULT_TEXT_ALIGNMENT : textAlign,
    })
    onUpdateEditorState(newEditorState)

    newEditorState = changeFontSize(newEditorState, DEFAULT_FONT_SIZE)
    onUpdateEditorState(newEditorState)

    newEditorState = changeFontColor(
      newEditorState,
      R.prop('fontColor', templateSelected),
    )
    onUpdateEditorState(newEditorState)

    onSelectTemplate(templateSelected)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateSelected])

  if (R.isNil(templateSelected)) {
    return (
      <div className={classes.templateLoading}>
        <CircularProgress />
      </div>
    )
  }
  return (
    <div
      className={clsx(classes.editor, isFocus ? classes.placeholderHide : '')}
      onClick={onFocusEditor}
    >
      <Editor
        editorRef={setEditorReference}
        wrapperClassName="demo-wrapper"
        editorClassName="demo-editor"
        placeholder={formatMessage({
          id: 'voucher.dialog.textEditor.placeholder',
        })}
        customStyleMap={styleMap}
        customStyleFn={customStyleFn}
        handleBeforeInput={handleBeforeInput}
        handlePastedText={handlePastedText}
        editorStyle={{
          border: '1px solid #C0C0C0',
          minHeight: '400px',
          backgroundImage: `url('${R.prop('imageUrl', templateSelected)}')`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          wordBreak: 'break-word',
          backgroundSize: '100% 100%',
          padding: '40px',
        }}
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        onBlur={onBlurEditor}
        toolbar={{
          options: ['fontSize', 'colorPicker', 'inline', 'textAlign'],
          inline: {
            inDropdown: false,
            className: undefined,
            component: undefined,
            dropdownClassName: undefined,
            options: ['bold', 'italic', 'underline', 'strikethrough'],
          },
          textAlign: {
            options: ['left', 'center', 'right'],
          },
          fontSize: {
            inDropdown: false,
            component: (props) => (
              <FontSizeSelector {...props} fontSize={DEFAULT_FONT_SIZE} />
            ),
          },
          colorPicker: {
            inDropdown: false,
            component: (props) => (
              <ColorSelector
                {...props}
                templateColor={
                  R.prop('fontColor', templateSelected) || DEFAULT_FONT_COLOR
                }
              />
            ),
          },
        }}
        readOnly={isDisabled}
        toolbarHidden={isDisabled}
        toolbarCustomButtons={[
          <DoNotTranslate
            editorState={editorState}
            onUpdateEditorState={onUpdateEditorState}
          />,
          <TemplateSelector
            templates={templates}
            onSelected={onSelectedTemplateFromState}
          />,
        ]}
      />
    </div>
  )
}

export default React.memo(TextEditorDraft)
